import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Send, X, MessageSquare } from 'lucide-react';

type Message = {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
};

type ChatbotResponse = {
  answer: string;
  followUp?: string[];
};

const initialMessages = [
  {
    id: '1',
    content: 'Hi there! I\'m Sarah\'s virtual assistant. How can I help you with your accounting needs today?',
    isUser: false,
    timestamp: new Date()
  }
];

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Scroll to bottom whenever messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Submit message to chatbot API
  const chatbotMutation = useMutation({
    mutationFn: async (question: string) => {
      const res = await apiRequest('POST', '/api/chatbot', { question });
      return res.json() as Promise<ChatbotResponse>;
    },
    onSuccess: (data) => {
      // Add bot response
      const botMessage: Message = {
        id: Date.now().toString(),
        content: data.answer,
        isUser: false,
        timestamp: new Date()
      };
      setMessages((prev) => [...prev, botMessage]);
      
      // Add follow-up suggestions if available
      if (data.followUp && data.followUp.length > 0) {
        const suggestionsMessage: Message = {
          id: `suggestions-${Date.now()}`,
          content: JSON.stringify(data.followUp),
          isUser: false,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, suggestionsMessage]);
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Something went wrong with the chatbot.',
        variant: 'destructive',
      });
      
      // Add error message from bot
      const errorMessage: Message = {
        id: Date.now().toString(),
        content: "I'm sorry, I'm having trouble connecting right now. Please try again later or contact us directly.",
        isUser: false,
        timestamp: new Date()
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() === '') return;
    
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      isUser: true,
      timestamp: new Date()
    };
    setMessages((prev) => [...prev, userMessage]);
    
    // Process with chatbot API
    chatbotMutation.mutate(inputValue);
    
    // Clear input
    setInputValue('');
  };

  const handleSuggestionClick = (suggestion: string) => {
    // Add suggestion as user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content: suggestion,
      isUser: true,
      timestamp: new Date()
    };
    setMessages((prev) => [...prev, userMessage]);
    
    // Process with chatbot API
    chatbotMutation.mutate(suggestion);
  };

  return (
    <>
      {/* Chatbot toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 bg-[#1a365d] text-white p-4 rounded-full shadow-lg z-50 flex items-center justify-center hover:bg-opacity-90 transition-all"
        aria-label="Toggle chatbot"
      >
        {isOpen ? <X size={24} /> : <MessageSquare size={24} />}
      </button>
      
      {/* Chatbot window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-80 md:w-96 bg-white rounded-lg shadow-xl z-50 flex flex-col h-[500px] overflow-hidden">
          {/* Header */}
          <div className="bg-[#1a365d] text-white p-4 flex justify-between items-center">
            <h3 className="font-medium">SW Accounting Assistant</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-gray-200"
              aria-label="Close chatbot"
            >
              <X size={18} />
            </button>
          </div>
          
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    message.isUser
                      ? 'bg-[#1a365d] text-white rounded-br-none'
                      : message.id.startsWith('suggestions')
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-[#f0f4f8] text-gray-800 rounded-bl-none'
                  }`}
                >
                  {message.id.startsWith('suggestions') ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium mb-2">Quick questions you can ask:</p>
                      {JSON.parse(message.content).map((suggestion: string, index: number) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="block w-full text-left p-2 text-sm bg-white hover:bg-gray-50 rounded border border-gray-200"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm">{message.content}</p>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input form */}
          <form onSubmit={handleSubmit} className="p-4 border-t">
            <div className="flex items-center space-x-2">
              <Textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your question here..."
                className="flex-1 min-h-[60px] resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
              <Button 
                type="submit" 
                size="icon"
                disabled={chatbotMutation.isPending}
                className="h-10 w-10 rounded-full bg-[#7d9b76] hover:bg-opacity-90"
              >
                {chatbotMutation.isPending ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}